import React, { useState, useEffect, useCallback } from 'react';
import { User, FileText, MessageSquare, Edit2 } from 'lucide-react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../hooks/use-toast';
import PostCard from '../components/PostCard';
import CreatePostModal from '../components/CreatePostModal';
import { useNavigate } from 'react-router-dom';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const UserProfile = () => {
  const [activeTab, setActiveTab] = useState('profile');
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [screenname, setScreenname] = useState('');
  const [myPosts, setMyPosts] = useState([]);
  const [myDrafts, setMyDrafts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingDraft, setEditingDraft] = useState(null);
  const { user, updateScreenname } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();

  const fetchMyPosts = useCallback(async () => {
    if (!user) return;
    try {
      const response = await axios.get(`${API}/posts?author_id=${user.id}`);
      setMyPosts(response.data.filter(p => !p.is_draft));
    } catch (error) {
      // Silent fail - posts will show as empty
    }
  }, [user]);

  const fetchMyDrafts = useCallback(async () => {
    if (!user) return;
    try {
      const response = await axios.get(`${API}/posts?author_id=${user.id}&include_drafts=true`);
      setMyDrafts(response.data.filter(p => p.is_draft));
    } catch (error) {
      // Silent fail - drafts will show as empty
    }
  }, [user]);

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }
    setName(user.name || '');
    setPhone(user.phone || '');
    setScreenname(user.screenname || '');
    
    if (activeTab === 'posts') fetchMyPosts();
    if (activeTab === 'drafts') fetchMyDrafts();
  }, [user, activeTab, navigate, fetchMyPosts, fetchMyDrafts]);

  const handleEditDraft = async (draft) => {
    try {
      // Fetch fresh draft data from API to ensure we have latest content
      const response = await axios.get(`${API}/posts/${draft.id}`);
      setEditingDraft(response.data);
      setShowEditModal(true);
    } catch (error) {
      toast({
        title: 'Error loading draft',
        description: 'Could not load the latest draft content',
        variant: 'destructive',
      });
    }
  };

  const handleDraftUpdated = (updatedPost) => {
    fetchMyDrafts();
    setEditingDraft(null);
    
    // If the post was published (is_draft = false), show success and optionally redirect
    if (updatedPost && !updatedPost.is_draft) {
      toast({
        title: 'Post published!',
        description: 'Your post has been submitted for review and will appear in the community feed once verified.',
      });
    }
  };

  const handleProfileUpdate = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await axios.patch(`${API}/auth/profile`, {
        name: name || null,
        phone: phone || null,
        screenname
      });

      toast({
        title: 'Profile updated!',
        description: 'Your profile has been saved successfully.',
      });
      
      window.location.reload();
    } catch (error) {
      toast({
        title: 'Update failed',
        description: error.response?.data?.detail || 'Please try again',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#F9F9F9] to-white py-8">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <h1 className="text-3xl font-bold text-[#7B3F9E] mb-6">My Profile</h1>

        {/* Tabs */}
        <div className="flex border-b border-gray-200 mb-6">
          <button
            onClick={() => setActiveTab('profile')}
            className={`px-6 py-3 font-medium transition ${
              activeTab === 'profile'
                ? 'text-[#FF8C00] border-b-2 border-[#FF8C00]'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            <User className="w-4 h-4 inline mr-2" />
            Profile Info
          </button>
          <button
            onClick={() => setActiveTab('posts')}
            className={`px-6 py-3 font-medium transition ${
              activeTab === 'posts'
                ? 'text-[#FF8C00] border-b-2 border-[#FF8C00]'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            <FileText className="w-4 h-4 inline mr-2" />
            My Posts
          </button>
          <button
            onClick={() => setActiveTab('drafts')}
            className={`px-6 py-3 font-medium transition ${
              activeTab === 'drafts'
                ? 'text-[#FF8C00] border-b-2 border-[#FF8C00]'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            <Edit2 className="w-4 h-4 inline mr-2" />
            My Drafts
          </button>
        </div>

        {/* Profile Info Tab */}
        {activeTab === 'profile' && (
          <div className="bg-white rounded-lg shadow-md p-6">
            <form onSubmit={handleProfileUpdate} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Screenname *
                </label>
                <input
                  type="text"
                  value={screenname}
                  onChange={(e) => setScreenname(e.target.value)}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#FF8C00] outline-none"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Name (Optional)
                </label>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Your real name"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#FF8C00] outline-none"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Phone (Optional)
                </label>
                <input
                  type="tel"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder="+1234567890"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#FF8C00] outline-none"
                />
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full bg-[#FF8C00] text-white py-3 rounded-lg hover:bg-[#E67E00] transition disabled:opacity-50"
              >
                {loading ? 'Saving...' : 'Save Profile'}
              </button>
            </form>
          </div>
        )}

        {/* My Posts Tab */}
        {activeTab === 'posts' && (
          <div className="space-y-4">
            {myPosts.length === 0 ? (
              <div className="text-center py-12 bg-white rounded-lg shadow-sm">
                <p className="text-gray-600">You haven't created any posts yet.</p>
              </div>
            ) : (
              myPosts.map(post => (
                <PostCard key={post.id} post={post} onUpdate={fetchMyPosts} onDelete={fetchMyPosts} />
              ))
            )}
          </div>
        )}

        {/* My Drafts Tab */}
        {activeTab === 'drafts' && (
          <div className="space-y-4">
            {myDrafts.length === 0 ? (
              <div className="text-center py-12 bg-white rounded-lg shadow-sm">
                <p className="text-gray-600">No draft posts.</p>
              </div>
            ) : (
              myDrafts.map(draft => (
                <div key={draft.id} className="bg-white rounded-lg shadow-md p-6 border-l-4 border-yellow-500">
                  <div className="flex justify-between items-start mb-4">
                    <div className="flex-1">
                      <div className="flex items-center space-x-2 mb-2">
                        <span className="text-xs px-2 py-1 rounded-full font-medium bg-gray-100 text-gray-700">
                          {draft.post_type}
                        </span>
                        <span className="text-xs text-yellow-600 font-medium">
                          DRAFT
                        </span>
                      </div>
                      <h3 className="text-xl font-semibold text-gray-900 mb-2">
                        {draft.title}
                      </h3>
                      <p className="text-gray-600 line-clamp-3">
                        {draft.body}
                      </p>
                    </div>
                  </div>
                  <div className="flex justify-end space-x-3">
                    <button
                      onClick={() => handleEditDraft(draft)}
                      className="flex items-center space-x-2 bg-[#FF8C00] text-white px-4 py-2 rounded-lg hover:bg-[#E67E00] transition"
                    >
                      <Edit2 className="w-4 h-4" />
                      <span>Edit & Publish</span>
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        )}

        {/* Edit Draft Modal */}
        <CreatePostModal
          isOpen={showEditModal}
          onClose={() => { setShowEditModal(false); setEditingDraft(null); }}
          onPostCreated={handleDraftUpdated}
          editPost={editingDraft}
        />
      </div>
    </div>
  );
};

export default UserProfile;
